import config from "react-global-configuration"
import { all, call as callEffect, put, select, takeEvery, takeLatest } from "redux-saga/effects"
import { push } from "redux-first-history";

import { getNextDoctor } from "client/optic/selectors"
import { SET_CUSTOMER, SET_RELATIVES } from "client/services/constants"
import { getCustomer } from "client/services/selector"
import { CALL_HAS_CHANGED } from "containers/Call/services/constants"
import { TLC_SUCCESS } from "containers/Payment/services/constants"
import { PATH } from "models/survey/_paths"
import { FOR_PATIENT } from "models/survey/constants"
import { Customer, DoctorType, Gender, Relative } from "types/entity"
import { Call, CallState } from "types/payload"
import { CustomerType } from "types/props"
import { Action, ActionWithoutPayload, GFlow, GWatcher } from "types/redux"
import { HARDWARE_ESSILOR_TYPE, SAVE_HARDWARE_TYPE } from "client/socket/optic/contants";

import { Answer, Question } from "../types"
import actions from "./actions"
import {
  FETCH_LATEST_ANAMNESIS,
  NEXT_STEP,
  PREVIOUS_STEP,
  SET_CURRENT_STEP,
  SET_STEP,
  STEP_PATIENT_CHOICE,
  STEP_RELATIVES_CHOICE,
} from "./constants"
import {
  buildAnamnesisPrefill,
  customerIdentity,
  fetchLatestAnamnesis,
  getLatestAnamnesisPatientId,
  LATEST_ANAMNESIS_CATEGORY,
  LatestAnamnesisCache,
} from "./latestAnamnesis"
import {
  getCurrentPatient,
  getCurrentPatientGender,
  getCurrentQuestion,
  getCurrentSelectedPatientId,
  getSurveyId,
  isSurveyEmpty,
} from "./selector"
import { COMPLETE_PATIENT_INFO_PAGE, SET_RELATIVE_PAGE } from "core/constants"
import { getMissingBirthTraits } from "lib/utils"

import { SocketStore, SurveyStore } from "types/store"
import { HARDWARE_NIDEK_TYPE, HARDWARE_NIDEK_AND_DRS_PLUS_TYPE } from "client/socket/optic/contants"


function* createRelativesQuestion({
  payload,
}: Action<Relative[]>): GFlow<Action<Relative[]>> {
  if (payload) {
    yield put({
      type: STEP_RELATIVES_CHOICE,
      payload,
    })
  } else yield put({ type: STEP_RELATIVES_CHOICE, payload: [] })
}

function* createPatientQuestion(
  action: Action<Customer>
): GFlow<Action<Customer>> {
  if (action.payload)
    yield all([put({ type: STEP_PATIENT_CHOICE, payload: action.payload })])
}

function* manageSurveyQuestions(): GFlow<
  Action<Question | "patient" | "relative">
> {
  const currentQuestionPath = yield select(getSurveyId)
  const hardwareType = yield select(({ socket }: { socket: SocketStore }) => socket?.hardwareType)
  if (hardwareType === HARDWARE_ESSILOR_TYPE || hardwareType === HARDWARE_NIDEK_TYPE || hardwareType === HARDWARE_NIDEK_AND_DRS_PLUS_TYPE) {
    yield put(actions.updateTimeline());
    yield put(actions.remove(PATH.timeline_tutorial));
    yield put(actions.remove(PATH.tutorial))
  }
  if (currentQuestionPath === PATH.patient) {
    const currentPatientGender = yield select(getCurrentPatientGender)
    const currentCustomerId = yield select(getCurrentSelectedPatientId)
    if (currentCustomerId !== undefined) {
      yield modifyTitleQuestion(currentCustomerId, currentPatientGender)
      yield askMissingPatientInfo(currentCustomerId)
      yield put(actions.fetchLatestAnamnesis())
    }
  }
}

function* loadLatestAnamnesis(): GFlow<any> {
  const patientId = getLatestAnamnesisPatientId(
    yield select(getCurrentSelectedPatientId)
  )
  const customerId = customerIdentity(yield select(getCustomer))
  if (patientId === undefined || customerId === undefined) return
  try {
    const data = yield callEffect(
      fetchLatestAnamnesis,
      LATEST_ANAMNESIS_CATEGORY,
      patientId
    )
    if (!data) return
    const selectedPatientId = getLatestAnamnesisPatientId(
      yield select(getCurrentSelectedPatientId)
    )
    if (
      selectedPatientId !== patientId ||
      customerIdentity(yield select(getCustomer)) !== customerId
    )
      return
    yield put(
      actions.setLatestAnamnesis({
        customerId,
        patientId,
        category: LATEST_ANAMNESIS_CATEGORY,
        data,
      })
    )
    yield callEffect(applyLatestAnamnesisPrefill)
  } catch (error) {
    console.error(error, { route: config.get("patient.get.latest_anamnesis") })
  }
}

function* applyLatestAnamnesisPrefill(): GFlow<any> {
  const latest: LatestAnamnesisCache | undefined = yield select(
    ({ survey }: { survey: SurveyStore }) => survey.latestAnamnesis
  )
  if (!latest) return
  const patientId = getLatestAnamnesisPatientId(
    yield select(getCurrentSelectedPatientId)
  )
  if (
    latest.patientId !== patientId ||
    latest.customerId !== customerIdentity(yield select(getCustomer))
  )
    return
  const question: Question | undefined = yield select(getCurrentQuestion)
  const { answers, prefillConsumed } = yield select(
    ({ survey }: { survey: SurveyStore }) => ({
      answers: survey.answers || [],
      prefillConsumed: survey.prefillConsumed,
    })
  )
  const prefill = buildAnamnesisPrefill(
    question,
    latest.data,
    answers as Answer[],
    prefillConsumed
  )
  if (prefill.length) yield put(actions.prefillAnamnesis(prefill))
}

function* askMissingPatientInfo(customerId: number): GFlow<any> {
  const patient: Customer | Relative | undefined = yield select(getCurrentPatient)
  if (!patient) return
  const isRelative = customerId !== FOR_PATIENT
  const missingGender =
    isRelative && ![Gender.MALE, Gender.FEMALE].includes(patient.gender)
  if (missingGender || getMissingBirthTraits(patient).length) {
    yield put(push(isRelative ? SET_RELATIVE_PAGE : COMPLETE_PATIENT_INFO_PAGE))
  }
}

function* modifyTitleQuestion(
  customerId: number,
  gender: Gender
): GFlow<Action<CustomerType>> {
  if (customerId === FOR_PATIENT) {
    yield put(actions.for("patient"))
    // customer is the main patient
  } else {
    // customer is a relative
    if (gender === Gender.MALE) yield put(actions.for("relative_male"))
    if (gender === Gender.FEMALE) yield put(actions.for("relative_female"))
  }
}

function* paymentSuccessFlow(
  action: Action<Call>
): GFlow<ActionWithoutPayload> {
  const call = action.payload
  if (call.category_id === DoctorType.ORTHOPTIST)
    yield put(actions.setCurrentStep(PATH.call_ortho_on_going))
  else if (call.category_id === DoctorType.OPHTALMOLOGIST)
    yield put(actions.setCurrentStep(PATH.call_ophta_on_going))
}

function* callHasChangedFlow(
  action: Action<Call>
): GFlow<ActionWithoutPayload> {
  const call = action.payload
  if (!call) return
  if (call.state === CallState.ENDED) yield put(actions.next())
  else if (
    call.state === CallState.CANCELLED_BY_DOCTOR ||
    call.state === CallState.CANCELLED_BY_PATIENT ||
    call.state === CallState.EXPIRED
  ) {
    // Call annulé / expiré

    const nextDoctor = yield select(getNextDoctor)
    const surveyIsEmpty = yield select(isSurveyEmpty)
    if (
      call.state === CallState.CANCELLED_BY_DOCTOR &&
      nextDoctor === DoctorType.ORTHOPTIST &&
      surveyIsEmpty
    ) {
      // si le call est annulé côté docteur orthoptiste, le medicalPath est cancelled, donc il ne possède pas l'anamnèse
      // Donc si par malheur le survey (anamnèse) est vide (parce que refresh par ex.), alors on doit repasser par les questions
      yield put(actions.setCurrentStep(PATH.timeline_initialize))
    } else yield put(actions.setCurrentStep(PATH.payment))
  } else {
    // Call en cours
    if (call.category_id === DoctorType.ORTHOPTIST)
      yield put(actions.setCurrentStep(PATH.call_ortho_on_going))
    if (call.category_id === DoctorType.OPHTALMOLOGIST)
      yield put(actions.setCurrentStep(PATH.call_ophta_on_going))
  }
}

function* surveyWatcher(): GWatcher {
  yield takeLatest(SET_RELATIVES, createRelativesQuestion)
  yield takeLatest(SET_CUSTOMER, createPatientQuestion)
  yield takeLatest(SET_STEP, manageSurveyQuestions)
  yield takeLatest(SAVE_HARDWARE_TYPE, manageSurveyQuestions);
  yield takeLatest(FETCH_LATEST_ANAMNESIS, loadLatestAnamnesis)
  yield takeEvery(
    [NEXT_STEP, PREVIOUS_STEP, SET_CURRENT_STEP],
    applyLatestAnamnesisPrefill
  )
  yield takeLatest(TLC_SUCCESS, paymentSuccessFlow)
  yield takeLatest(CALL_HAS_CHANGED, callHasChangedFlow)
}

export default surveyWatcher
